import React, { useState } from 'react';
import type { EpicDiagnosis, StoryDiagnosis } from '../../../../shared/types/epics';

/**
 * FORGE-154: "Why is this stuck?" card.
 *
 * Renders the per-story diagnosis the backend computes via the shared classifier
 * (the same one the orchestrator stamps onto rows at block points). Unlike the
 * activity-derived PendingWorkBanner, this reads `epic.diagnosis` straight from
 * the detail payload and — crucially — shows the EXACT operator command to fix
 * each stuck story, copy-to-clipboard. Turns "the page renders silence / I have
 * to dig through CloudWatch" into one glance + one paste.
 */

const SEVERITY_STYLE: Record<StoryDiagnosis['severity'], { box: string; chip: string; label: string }> = {
  critical: { box: 'border-rose-300 bg-rose-50', chip: 'bg-rose-100 text-rose-800', label: 'text-rose-900' },
  warning:  { box: 'border-amber-300 bg-amber-50', chip: 'bg-amber-100 text-amber-800', label: 'text-amber-900' },
  info:     { box: 'border-sky-300 bg-sky-50', chip: 'bg-sky-100 text-sky-800', label: 'text-sky-900' },
};

const KIND_LABEL: Record<StoryDiagnosis['kind'], string> = {
  merge_blocked:     'Merge failed',
  needs_answer:      'Needs your answer',
  force_complete:    'Force-completed',
  blocked:           'Blocked',
  testing_failed:    'Tests failed',
  stalled:           'Stalled',
  awaiting_approval: 'Awaiting approval',
};

function stuckLabel(mins: number): string {
  if (!Number.isFinite(mins)) return '';
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
}

const CommandLine: React.FC<{ command: string }> = ({ command }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <code className="flex-1 font-mono text-2xs rounded bg-white/80 border border-slate-200 px-2 py-1 overflow-x-auto whitespace-nowrap">
        {command}
      </code>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 text-2xs font-medium rounded border border-slate-300 bg-white px-2 py-1 hover:bg-slate-50"
        aria-label="Copy command"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const StoryRow: React.FC<{ d: StoryDiagnosis }> = ({ d }) => {
  const s = SEVERITY_STYLE[d.severity];
  return (
    <li className={`rounded-lg border ${s.box} px-3 py-2`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-2xs font-semibold rounded px-1.5 py-0.5 ${s.chip}`}>{KIND_LABEL[d.kind]}</span>
        {d.storyRef && <span className="font-mono text-2xs text-slate-600">story {d.storyRef}</span>}
        {Number.isFinite(d.stuckMinutes) && d.stuckMinutes > 0 && (
          <span className="text-2xs text-slate-500">· stuck {stuckLabel(d.stuckMinutes)}</span>
        )}
      </div>
      <div className={`text-xs mt-1 ${s.label}`}>{d.summary}</div>
      {d.detail && <div className="text-2xs text-slate-600 mt-0.5 whitespace-pre-wrap break-words">{d.detail}</div>}
      {d.suggestedCommand && <CommandLine command={d.suggestedCommand} />}
    </li>
  );
};

export const StuckDiagnosisCard: React.FC<{ diagnosis?: EpicDiagnosis | null }> = ({ diagnosis }) => {
  if (!diagnosis || diagnosis.stories.length === 0) return null;
  const crit = diagnosis.stories.filter((d) => d.severity === 'critical').length;
  return (
    <section
      data-testid="stuck-diagnosis-card"
      className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-2"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Why is this stuck?</h2>
        {diagnosis.headline && (
          <span className={`text-2xs font-medium rounded-full px-2 py-0.5 ${crit > 0 ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>
            {diagnosis.headline}
          </span>
        )}
      </div>
      <ul className="space-y-1.5">
        {diagnosis.stories.map((d, i) => (
          <StoryRow key={`${d.storyRef ?? 'story'}-${d.kind}-${i}`} d={d} />
        ))}
      </ul>
      <p className="text-2xs text-slate-400">Run the suggested command on the epic issue to resolve each item.</p>
    </section>
  );
};

export default StuckDiagnosisCard;
